/**
 * **A weight goal's series** on its desk card: the client's body weight since the goal was set
 * (`model/goalProgress`'s `goalWeightPoints`), the newest reading beside the title (owner,
 * 26 September 2026). The Pomiary tab's own history read, one cache entry for one client.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { clientBodyHistoryQueryOptions } from '~/entities/client';
import { numberFormat } from '~/shared/lib/intlCache';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { TrendCard } from '~/shared/ui/chart/TrendCard';

import { goalWeightPoints } from '../model/goalProgress';

interface GoalWeightTrendProps {
  readonly clientId: number;
  /** The civil day the goal was set, where the payload names one. */
  readonly since: string | null;
  /** A reduction goal reads lower as better. */
  readonly better: 'higher' | 'lower';
  readonly reducedMotion: boolean;
}

export function GoalWeightTrend({ clientId, since, better, reducedMotion }: GoalWeightTrendProps) {
  const { t, i18n } = useTranslation();
  const history = useQuery(clientBodyHistoryQueryOptions(clientId));
  const gate = useSkeletonGate(history.isPending);
  const points = history.data == null ? [] : goalWeightPoints(history.data.items ?? [], since);
  const unit = t('web:forma.unit.weight');
  const newest = points.at(-1);

  return (
    <TrendCard
      title={t('web:tasks.blocks.goal.weightTitle')}
      gate={gate}
      failed={history.isError}
      failedTitle={t('web:tasks.blocks.goal.weightFailed')}
      emptyTitle={t('web:tasks.blocks.goal.weightEmpty')}
      series={
        newest === undefined
          ? null
          : {
              name: t('web:tasks.blocks.goal.weight'),
              unit,
              better,
              figure: null,
              points,
              latest: `${numberFormat(i18n.language, { maximumFractionDigits: 1 }).format(newest.y)} ${unit}`,
            }
      }
      reducedMotion={reducedMotion}
    />
  );
}
